import { FormikProvider, useFormik } from 'formik'
import { omit } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Container, Form, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import projects from '../../api/projects/hooks'
import FormActionButtons from '../../Components/Common/FormActionButtons'
import PageTitle from '../../Components/Common/PageTitle'
import { useFormValidationErrorModal } from '../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import ProjectFormDescriptionTab, {
    ProjectFormDescriptionTabMain,
    ProjectFormDescriptionTabSide,
} from '../../Components/ProjectFormDescriptionTab/ProjectFormDescriptionTab'
import ProjectFormGeneralTab, {
    ProjectFormGeneralTabMain,
    ProjectFormGeneralTabSide,
} from '../../Components/ProjectFormGeneralTab/ProjectFormGeneralTab'
import { resetFormFilters } from '../../store/formFilters'
import { changeTranslationObject } from '../../utils/general'
import { projectGeneralFormTabs } from './constants'
import { useProjectDescriptionFormConfig, useProjectGeneralFormConfig } from './hooks'

const ProjectCreate = () => {
    const history = useHistory()
    const location = useLocation()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    // tabs definition
    const tabs = projectGeneralFormTabs(false)

    const [activeTab, setActiveTab] = useState(tabs[0]?.name)

    const { mutate: createProject } = projects.CreateUpdateProject.useMutation()

    const { generalInputs, generalValidationConfig } = useProjectGeneralFormConfig(null)
    const { descriptionInputs, descriptionValidationConfig } = useProjectDescriptionFormConfig(null)

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            general: generalValidationConfig.initialValues ?? {},
            description: descriptionValidationConfig.initialValues ?? {},
        },
        validationSchema: Yup.object({
            general: generalValidationConfig.validationSchema ?? Yup.object({}),
            description: descriptionValidationConfig.validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values) => {
            const generalValues = generalValidationConfig.prepareModelForSubmit(values.general)
            const filteredValues = omit(generalValues, ['id'])
            const translations = changeTranslationObject(values.description, 'project_title', 'project_description', 'project_offer_description')

            const submitValues = {
                ...filteredValues,
                ...values.description,
                translations,
            }

            createProject({ data: submitValues, isEdit: false })
        },
    })

    useFormValidationErrorModal(formik, [generalInputs, descriptionInputs])

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters())
        }
    }, [dispatch])

    const handleTabChange = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const renderFormButtons = () => <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push('/projects')} />

    return (
        <div className="page-content">
            <Container fluid>
                <FormikProvider value={formik}>
                    <Form onSubmit={(e) => formik.handleSubmit(e)}>
                        <PageTitle title={t('page.newProject')}>{renderFormButtons()}</PageTitle>

                        <TabContent activeTab={activeTab} className="text-muted">
                            <TabPane tabId="general">
                                <ProjectFormGeneralTab
                                    subform="general"
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    inputs={generalInputs}
                                    formik={formik}
                                    module={location}
                                    owner={null}
                                    handleTabChange={handleTabChange}
                                >
                                    <ProjectFormGeneralTabSide />
                                    <ProjectFormGeneralTabMain />
                                </ProjectFormGeneralTab>
                            </TabPane>
                            <TabPane tabId="description">
                                <ProjectFormDescriptionTab
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    inputs={descriptionInputs}
                                    subform="description"
                                    formik={formik}
                                    handleTabChange={handleTabChange}
                                >
                                    <ProjectFormDescriptionTabSide />
                                    <ProjectFormDescriptionTabMain />
                                </ProjectFormDescriptionTab>
                            </TabPane>
                        </TabContent>
                    </Form>
                </FormikProvider>
            </Container>
        </div>
    )
}

export default ProjectCreate
